import React from "react";
import styled from "styled-components";
import Tooltip from "@material-ui/core/Tooltip";
import { T, FONTS, FS, BP } from "../tokens";
import { Icon } from "./Icons";
import { Badge, Dot, CheckToggle, Spinner } from "./Primitives";

export const sourceChip = (group) => {
  switch (group) {
    case "Claimant":
      return {
        bg: "#EFF6FF",
        fg: "#1E40AF",
        dot: "#3B82F6",
        border: "#93C5FD",
      };
    case "Respondent":
      return {
        bg: "#FEF2F2",
        fg: "#991B1B",
        dot: "#EF4444",
        border: "#FCA5A5",
      };
    default:
      return { bg: T.navyTint, fg: T.navy, dot: T.mute, border: T.line };
  }
};

const DocPagePicker = ({
  doc,
  picked,
  isExpanded,
  loadingPages = false,
  onToggleExpand,
  onToggleAll,
  onSetPicked,
}) => {
  const chip = sourceChip(doc.source);
  const totalPgs = doc.pages || 0;
  const allPicked = totalPgs > 0 && picked.size === totalPgs;
  const some = picked.size > 0 && !allPicked;

  const togglePage = (n) => {
    const next = new Set(picked);
    next.has(n) ? next.delete(n) : next.add(n);
    onSetPicked(Array.from(next));
  };

  const selectAll = () =>
    onSetPicked(Array.from({ length: totalPgs }, (_, i) => i + 1));
  const clearAll = () => onSetPicked([]);

  return (
    <DocItem>
      <DocRow onClick={onToggleExpand}>
        <Tooltip
          title={allPicked ? "Deselect all pages" : "Select all pages"}
          arrow
        >
          <CheckToggle
            $on={allPicked}
            onClick={(e) => {
              e.stopPropagation();
              onToggleAll();
            }}
          >
            {allPicked && <Icon.Check size={12} color={T.white} />}
            {some && (
              <span
                style={{
                  height: 2,
                  width: 9,
                  background: T.teal,
                  borderRadius: 1,
                }}
              />
            )}
          </CheckToggle>
        </Tooltip>

        <Icon.File size={15} color={T.mute} />

        <DocInfo>
          <DocName title={doc.name}>{doc.name}</DocName>
          <DocMeta>
            <span>{doc.category}</span>
            <span>·</span>
            <PageCountBadge>
              {loadingPages ? "…" : `${totalPgs} pages`}
            </PageCountBadge>
            <span>·</span>
            <span>{doc.date}</span>
            {doc.ext && <span>· .{doc.ext}</span>}
          </DocMeta>
        </DocInfo>

        <Badge
          $bg={chip.bg}
          $fg={chip.fg}
          style={{ border: `1px solid ${chip.border}` }}
        >
          <Dot $color={chip.dot} />
          {doc.source}
        </Badge>

        {picked.size > 0 && (
          <Badge $bg={T.tealTint} $fg={T.tealDark}>
            {picked.size} picked
          </Badge>
        )}

        <ExpandBtn aria-expanded={isExpanded}>
          {isExpanded ? (
            <Icon.ChevronUp size={14} color={T.navy} />
          ) : (
            <Icon.ChevronDown size={14} color={T.navy} />
          )}
        </ExpandBtn>
      </DocRow>

      {/* Page Selection Grid */}
      {isExpanded &&
        (loadingPages ? (
          <LoadingRow>
            <Spinner $size="18px" />
            <span>Fetching page count...</span>
          </LoadingRow>
        ) : (
          <PageGrid>
            <PageGridHeader>
              <span>{totalPgs} pages available</span>
              <div style={{ display: "flex", gap: 12 }}>
                <button
                  onClick={selectAll}
                  style={{
                    color: T.teal,
                    fontSize: FS.xs,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  Select All
                </button>
                {picked.size > 0 && (
                  <button
                    onClick={clearAll}
                    style={{
                      color: T.mute,
                      fontSize: FS.xs,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
            </PageGridHeader>

            {Array.from({ length: totalPgs }, (_, i) => i + 1).map((n) => (
              <PageBtn
                key={n}
                $on={picked.has(n)}
                onClick={() => togglePage(n)}
                aria-pressed={picked.has(n)}
              >
                {n}
              </PageBtn>
            ))}

            {totalPgs === 0 && (
              <span
                style={{ fontSize: FS.xs, color: T.mute, padding: "10px 0" }}
              >
                No pages available for this document.
              </span>
            )}
          </PageGrid>
        ))}
    </DocItem>
  );
};

export default DocPagePicker;

const DocItem = styled.div`
  border-bottom: 1px solid ${T.line};
  &:last-child {
    border-bottom: none;
  }
`;

const DocRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: ${T.white};
  transition: background 0.2s ease;
  cursor: pointer;

  &:hover {
    background: ${T.soft};
  }

  @media (max-width: ${BP.mobile}) {
    padding: 12px 14px;
    gap: 10px;
  }
`;

const DocInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const DocName = styled.div`
  font-family: ${FONTS.primaryFontSemiBold};
  font-size: ${FS.base};
  font-weight: 600;
  color: ${T.ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
`;

const DocMeta = styled.div`
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.xs};
  color: ${T.mute};
  margin-top: 3px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const PageCountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: ${FONTS.primaryFontSemiBold};
  font-size: ${FS.xs};
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 6px;
  background: ${T.navyTint};
  color: ${T.navy};
`;

const ExpandBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1.5px solid ${T.line};
  background: ${T.white};
  display: grid;
  place-items: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover {
    background: ${T.soft};
    border-color: ${T.teal};
  }

  @media (max-width: ${BP.mobile}) {
    width: 28px;
    height: 28px;
  }
`;

const PageGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 12px 16px 16px 52px;
  background: ${T.soft};
  border-top: 1px dashed ${T.line};

  @media (max-width: ${BP.mobile}) {
    padding-left: 16px;
    gap: 5px;
  }
`;

const PageGridHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font-family: ${FONTS.primaryFontSemiBold};
  font-size: ${FS.xs};
  color: ${T.mute};
`;

const PageBtn = styled.button`
  height: 30px;
  min-width: 34px;
  padding: 0 8px;
  border-radius: 6px;
  border: 1.5px solid ${(p) => (p.$on ? T.teal : T.line)};
  background: ${(p) => (p.$on ? T.teal : T.white)};
  color: ${(p) => (p.$on ? T.white : T.ink)};
  font-family: ${FONTS.primaryFontSemiBold};
  font-size: ${FS.xs};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${T.teal};
    background: ${(p) => (p.$on ? T.tealDark : T.tealTint)};
  }

  @media (max-width: ${BP.mobile}) {
    height: 28px;
    min-width: 30px;
  }
`;

const LoadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 16px 16px 52px;
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.xs};
  color: ${T.mute};
  background: ${T.soft};
  border-top: 1px dashed ${T.line};
`;
